import React, { useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export interface MusicTrack {
  id: string;
  source: string;
  file_url: string;
  file_name: string | null;
  duration: number | null;
  volume: number;
  start_offset: number;
  fade_in: number;
  fade_out: number;
}

interface MusicPanelProps {
  shortId: string;
  music: MusicTrack | null;
  originalAudioVolume: number;
  onMusicSet: (music: MusicTrack) => void;
  onMusicRemove: () => void;
  onMusicUpdate: (music: MusicTrack) => void;
  onOriginalVolumeChange: (vol: number) => void;
  onClose: () => void;
}

const LIBRARY_TRACKS = [
  { name: 'Upbeat Energy', file: '/music/upbeat-energy.mp3', duration: 30 },
  { name: 'Chill Lo-Fi', file: '/music/chill-lofi.mp3', duration: 45 },
  { name: 'Cinematic Rise', file: '/music/cinematic-rise.mp3', duration: 20 },
  { name: 'Happy Acoustic', file: '/music/happy-acoustic.mp3', duration: 35 },
  { name: 'Dark Ambient', file: '/music/dark-ambient.mp3', duration: 40 },
];

const MusicPanel: React.FC<MusicPanelProps> = ({
  shortId, music, originalAudioVolume,
  onMusicSet, onMusicRemove, onMusicUpdate, onOriginalVolumeChange, onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [playingLibrary, setPlayingLibrary] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('File must be under 10MB');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/api/shorts/${shortId}/music/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json();
    setUploading(false);
    if (res.ok) onMusicSet(data);
  };

  const handleLibrarySelect = async (track: typeof LIBRARY_TRACKS[0]) => {
    const res = await fetch(`${API_BASE}/api/shorts/${shortId}/music/library`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileUrl: track.file,
        fileName: track.name,
        duration: track.duration,
      }),
    });
    const data = await res.json();
    if (res.ok) onMusicSet(data);
  };

  const handlePreviewToggle = (file: string) => {
    if (previewAudio && playingLibrary === file) {
      previewAudio.pause();
      setPreviewAudio(null);
      setPlayingLibrary(null);
      return;
    }
    if (previewAudio) previewAudio.pause();
    const audio = new Audio(`${API_BASE}${file}`);
    audio.volume = 0.5;
    audio.play();
    audio.onended = () => { setPlayingLibrary(null); setPreviewAudio(null); };
    setPreviewAudio(audio);
    setPlayingLibrary(file);
  };

  const handleRemove = async () => {
    await fetch(`${API_BASE}/api/shorts/${shortId}/music`, {
      method: 'DELETE',
      credentials: 'include',
    });
    onMusicRemove();
  };

  const handleVolumeChange = (volume: number) => {
    if (!music) return;
    const updated = { ...music, volume };
    onMusicUpdate(updated);
    // Debounced save could be added here
    fetch(`${API_BASE}/api/shorts/${shortId}/music`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ volume }),
    });
  };

  const handleFadeChange = (key: 'fade_in' | 'fade_out', value: number) => {
    if (!music) return;
    const updated = { ...music, [key]: value };
    onMusicUpdate(updated);
    fetch(`${API_BASE}/api/shorts/${shortId}/music`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [key === 'fade_in' ? 'fadeIn' : 'fadeOut']: value }),
    });
  };

  return (
    <div className="music-panel">
      <div className="panel-header">
        <h3>Music</h3>
        <button className="btn-icon" onClick={onClose}>✕</button>
      </div>

      {/* Current track */}
      {music && (
        <div className="music-current">
          <div className="music-current-info">
            <span className="music-icon">♫</span>
            <span className="music-name">{music.file_name || 'Music Track'}</span>
          </div>
          <div className="control-row">
            <label>Volume</label>
            <input
              type="range" min="0" max="1" step="0.05"
              value={music.volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
            />
            <span>{Math.round(music.volume * 100)}%</span>
          </div>
          <button className="btn-sm btn-danger" onClick={handleRemove}>Remove Track</button>
        </div>
      )}

      {/* Upload */}
      <div className="music-upload-section">
        <h4>Upload Your Track</h4>
        <label className="music-dropzone" onClick={() => fileInputRef.current?.click()}>
          <span>📁 Drop audio file or click to upload</span>
          <span className="dropzone-hint">mp3, wav, m4a · 10MB max</span>
          {uploading && <span className="upload-spinner">Uploading...</span>}
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a"
          onChange={handleUpload}
          hidden
        />
      </div>

      {/* Library */}
      <div className="music-library-section">
        <h4>Stock Library</h4>
        <div className="music-library-list">
          {LIBRARY_TRACKS.map(track => (
            <div key={track.file} className="music-library-item">
              <span className="music-icon">♫</span>
              <span className="music-name">{track.name}</span>
              <span className="music-duration">{track.duration}s</span>
              <button
                className="btn-icon"
                onClick={() => handlePreviewToggle(track.file)}
              >
                {playingLibrary === track.file ? '⏸' : '▶'}
              </button>
              <button className="btn-sm" onClick={() => handleLibrarySelect(track)}>Use</button>
            </div>
          ))}
        </div>
      </div>

      {/* Mix controls */}
      <div className="music-mix-section">
        <h4>Mix</h4>
        <div className="control-row">
          <label>Original audio</label>
          <input
            type="range" min="0" max="1" step="0.05"
            value={originalAudioVolume}
            onChange={(e) => onOriginalVolumeChange(parseFloat(e.target.value))}
          />
          <span>{Math.round(originalAudioVolume * 100)}%</span>
        </div>
        {music && (
          <>
            <div className="control-row">
              <label>Fade in</label>
              <select value={music.fade_in} onChange={(e) => handleFadeChange('fade_in', parseFloat(e.target.value))}>
                <option value="0">0s</option>
                <option value="0.5">0.5s</option>
                <option value="1">1s</option>
                <option value="2">2s</option>
              </select>
            </div>
            <div className="control-row">
              <label>Fade out</label>
              <select value={music.fade_out} onChange={(e) => handleFadeChange('fade_out', parseFloat(e.target.value))}>
                <option value="0">0s</option>
                <option value="0.5">0.5s</option>
                <option value="1">1s</option>
                <option value="2">2s</option>
              </select>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MusicPanel;
